import React, { useCallback, useEffect, useRef, useState } from 'react';
import { resolveActiveSearcherId, visibleSearchers } from 'shared/searchers';
import { toCssUrl } from '../../heroStackStyle';
import HeroSearchCard from '../../HeroSearchCard';
import type { HeroTemplateProps } from '../../types';
import {
  ECOMMERCE_SLIDER_CARD_COLOR_DEFAULT,
  ECOMMERCE_SLIDER_INTERVAL_MS,
  normalizeEcommerceSliderSlides,
} from './sliderSlides';
import './HeroEcommerceSlider.css';

const SLIDER_FALLBACK_IMAGE = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1600 900"><defs><linearGradient id="s" x1="0" y1="0" x2="0" y2="1"><stop stop-color="%2387b9d4"/><stop offset="1" stop-color="%23eef4f7"/></linearGradient><linearGradient id="m" x1="0" y1="0" x2="1" y2="1"><stop stop-color="%2332475d"/><stop offset="1" stop-color="%237c94a5"/></linearGradient></defs><rect width="1600" height="900" fill="url(%23s)"/><path d="M0 720L250 420l150 170 200-290 240 330 220-250 260 340 280-220 200 250v150H0z" fill="url(%23m)"/></svg>')`;

function slideBackground(url?: string): string {
  const trimmed = (url || '').trim();
  return trimmed ? toCssUrl(trimmed) : SLIDER_FALLBACK_IMAGE;
}

const HeroEcommerceSlider: React.FC<HeroTemplateProps> = ({
  config,
  onSearcherSelect,
  renderSearchForm,
  paisId,
}) => {
  const slides = normalizeEcommerceSliderSlides(config.slides);
  const searchers = visibleSearchers(config.searcherTabs || []);
  const activeSearcherId = resolveActiveSearcherId(
    config.searcherTabs || [],
    config.activeSearcherTabId,
  );
  const cardColor =
    typeof config.searcherCardColor === 'string' && config.searcherCardColor.trim()
      ? config.searcherCardColor
      : ECOMMERCE_SLIDER_CARD_COLOR_DEFAULT;
  const [index, setIndex] = useState(0);
  const hoveringRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const tabsRef = useRef<HTMLElement>(null);

  const stopAutoplay = useCallback(() => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startAutoplay = useCallback(() => {
    stopAutoplay();
    if (slides.length < 2) return;
    timerRef.current = window.setInterval(() => {
      if (hoveringRef.current) return;
      setIndex((current) => (current + 1) % slides.length);
    }, ECOMMERCE_SLIDER_INTERVAL_MS);
  }, [slides.length, stopAutoplay]);

  useEffect(() => {
    setIndex((current) => (current >= slides.length ? 0 : current));
  }, [slides.length]);

  useEffect(() => {
    startAutoplay();
    return stopAutoplay;
  }, [startAutoplay, stopAutoplay]);

  function goTo(next: number) {
    const count = slides.length;
    if (!count) return;
    setIndex(((next % count) + count) % count);
    startAutoplay();
  }

  function scrollSearchersNext() {
    const nav = tabsRef.current;
    if (!nav) return;
    const amount = Math.max(180, nav.clientWidth * 0.65);
    const atEnd = nav.scrollLeft + nav.clientWidth >= nav.scrollWidth - 8;
    nav.scrollTo({ left: atEnd ? 0 : nav.scrollLeft + amount, behavior: 'smooth' });
  }

  const stageClass = [
    'header-slider-stage',
    searchers.length === 0 && 'header-slider-stage--carousel-only',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={stageClass} style={{ '--slider-card-color': cardColor } as React.CSSProperties}>
      {searchers.length > 0 ? (
        <div className='header-slider-search'>
          <div className='header-slider-search__tabs'>
            <nav aria-label='Buscadores' className='header-slider-tabs' ref={tabsRef}>
              {searchers.map((searcher) => {
                const iconStyle = {
                  '--searcher-icon-url': `url("${searcher.iconUrl}")`,
                } as React.CSSProperties;
                if (searcher.urlOnly) {
                  return (
                    <a
                      className='header-slider-tabs__link'
                      href={searcher.url || '#'}
                      key={searcher.id}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      <span className='header-slider-tabs__icon' style={iconStyle} />
                      <span>{searcher.label}</span>
                    </a>
                  );
                }
                const isActive = searcher.id === activeSearcherId;
                return (
                  <button
                    className={[
                      'header-slider-tabs__btn',
                      isActive && 'header-slider-tabs__btn--active',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    key={searcher.id}
                    onClick={() => onSearcherSelect?.(searcher.id)}
                    type='button'
                  >
                    <span className='header-slider-tabs__icon' style={iconStyle} />
                    <span>{searcher.label}</span>
                  </button>
                );
              })}
            </nav>
            {searchers.length > 1 ? (
              <button
                aria-label='Ver más buscadores'
                className='header-slider-tabs__next'
                onClick={scrollSearchersNext}
                type='button'
              >
                ›
              </button>
            ) : null}
          </div>
          <HeroSearchCard
            activeSearcherId={activeSearcherId}
            activeSearcherLabel=''
            benefits={[]}
            paisId={paisId}
            renderSearchForm={renderSearchForm}
            showTitle={false}
            title=''
          />
        </div>
      ) : null}

      <div
        className={['header-slider-carousel', slides.length < 2 && 'header-slider-carousel--single']
          .filter(Boolean)
          .join(' ')}
        onMouseEnter={() => {
          hoveringRef.current = true;
        }}
        onMouseLeave={() => {
          hoveringRef.current = false;
        }}
      >
        <div className='header-slider-track'>
          {slides.map((slide, slideIndex) => {
            const desktopImage = slideBackground(slide.imageUrl);
            const mobileImage = (slide.mobileImageUrl || '').trim()
              ? slideBackground(slide.mobileImageUrl)
              : desktopImage;
            const buttonText = (slide.buttonText || '').trim();
            const buttonUrl = (slide.buttonUrl || '').trim();
            return (
              <div
                className={[
                  'header-slider-slide',
                  slideIndex === index && 'header-slider-slide--active',
                ]
                  .filter(Boolean)
                  .join(' ')}
                key={slideIndex}
                style={
                  {
                    '--slide-image': desktopImage,
                    '--slide-image-mobile': mobileImage,
                  } as React.CSSProperties
                }
              >
                {(slide.tag || '').trim() ? (
                  <span className='header-slider-slide__tag'>{slide.tag}</span>
                ) : null}
                {(slide.title || '').trim() ? (
                  <h2 className='header-slider-slide__title'>{slide.title}</h2>
                ) : null}
                {(slide.description || '').trim() ? (
                  <p className='header-slider-slide__text'>{slide.description}</p>
                ) : null}
                {buttonText ? (
                  buttonUrl ? (
                    <a
                      className='header-slider-slide__cta'
                      href={buttonUrl}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      {buttonText}
                    </a>
                  ) : (
                    <span className='header-slider-slide__cta'>{buttonText}</span>
                  )
                ) : null}
              </div>
            );
          })}
        </div>
        {slides.length > 1 ? (
          <>
            <button
              aria-label='Slide anterior'
              className='header-slider-arrow header-slider-arrow--prev'
              onClick={() => goTo(index - 1)}
              type='button'
            >
              ‹
            </button>
            <button
              aria-label='Slide siguiente'
              className='header-slider-arrow header-slider-arrow--next'
              onClick={() => goTo(index + 1)}
              type='button'
            >
              ›
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default HeroEcommerceSlider;
